import { isAddress } from 'viem';

import { EVMNft } from '@repo/types-client';

import { PassABI } from '../abi';
import { BaseAlchemyProvider, EthereumAlchemyProvider } from '../providers';
import { getEvmNFTCollectionByAddress } from './get-evm-nft-collection-by-address';

type TokenResult = Record<string, string>;
type AddressResult = Record<string, TokenResult>;

/**
 * Result object containing token balances for multiple addresses and contracts
 */
type BalanceResult = {
  /** Nested record of contract -> address -> tokenId -> balance mappings */
  balances: Record<string, AddressResult>;
  /**
   * Gets the total balance of a specific token across all addresses
   * @param contractAddress - The ERC1155 contract address
   * @param tokenId - The token ID to check
   * @returns The total balance across all addresses
   */
  getAggregateBalance(
    contractAddress: string,
    tokenId: string | number,
  ): number;
  /**
   * Gets the balance of a specific token for a single address
   * @param address - The wallet address to check
   * @param contractAddress - The ERC1155 contract address
   * @param tokenId - The token ID to check
   * @returns The balance for the specified address
   */
  getAddressBalance(
    address: string,
    contractAddress: string,
    tokenId: string | number,
  ): number;
};

/**
 * Fetches ERC1155 token balances in batch for multiple addresses and tokens
 * @param addresses - Array of wallet addresses to check balances for
 * @param context - Map of contract addresses to array of token IDs or EVMNft objects
 * @returns Promise resolving to BalanceResult containing all balances and helper methods
 */
export const getBalanceOfBatch = async (
  addresses: string[],
  context: Record<string, (string | number | EVMNft)[]>,
): Promise<BalanceResult> => {
  // Filter for valid addresses
  addresses = addresses.filter((x) => isAddress(x));

  // Prepare batch query payloads
  const payloads = Object.keys(context).map((contractAddress) => {
    // Get unique token IDs for this contract
    const tokenIds = [
      ...new Set(
        context[contractAddress]?.map((x) => {
          if (typeof x === 'string' || typeof x === 'number')
            return x.toString();
          return x.tokenId.toString();
        }),
      ),
    ];
    const addressPayload: string[] = [];
    const tokenIdPayload: string[] = [];

    // Create cartesian product of addresses and tokenIds
    for (const address of addresses) {
      for (const tokenId of tokenIds) {
        addressPayload.push(address.toLowerCase());
        tokenIdPayload.push(tokenId);
      }
    }

    return {
      contractAddress: contractAddress.toLowerCase(),
      data: [addressPayload, tokenIdPayload],
    };
  });

  /**
   * Gets the appropriate provider based on the contract's chain
   */
  const getProvider = (contractAddress: string): any => {
    const collection = getEvmNFTCollectionByAddress(
      contractAddress as `0x${string}`,
    );
    if (collection?.chain === 'ethereum') {
      return EthereumAlchemyProvider;
    }
    return BaseAlchemyProvider;
  };

  // Execute all balance queries in parallel
  const results = await Promise.all(
    payloads.map(async (x) => ({
      contractAddress: x.contractAddress,
      balances: (await getProvider(x.contractAddress).readContract({
        address: x.contractAddress,
        abi: PassABI, // Any ERC1155 ABI will work
        functionName: 'balanceOfBatch',
        args: x.data,
      })) as bigint[],
    })),
  );

  const response: BalanceResult = {
    balances: {},
    getAddressBalance(
      address: string,
      contractAddress: string,
      tokenId: string | number,
    ) {
      address = address.toLowerCase();
      contractAddress = contractAddress.toLowerCase();
      if (!(contractAddress in this.balances)) return 0;
      const contractBalances = this.balances[contractAddress];
      if (!contractBalances || !(address in contractBalances)) return 0;
      const addressBalances = contractBalances[address];
      if (!addressBalances) return 0;
      return Number(addressBalances[tokenId.toString()] || 0);
    },
    getAggregateBalance(contractAddress: string, tokenId: string | number) {
      contractAddress = contractAddress.toLowerCase();
      if (!(contractAddress in this.balances)) return 0;
      let balance = 0;
      const contractBalances = this.balances[contractAddress];
      if (!contractBalances) return 0;
      for (const token of Object.values(contractBalances)) {
        balance += Number(token[tokenId.toString()] || 0);
      }
      return balance;
    },
  };

  // Process results into normalized response format
  for (const result of results) {
    const payload = payloads.find(
      (x) => x.contractAddress === result.contractAddress,
    );
    if (!payload) continue;

    // Initialize contract data if needed
    let contractData = response.balances[result.contractAddress];
    if (!contractData) {
      contractData = {};
      response.balances[result.contractAddress] = contractData;
    }

    // Map balances to their respective addresses and token IDs
    for (const [index, balance] of result.balances.entries()) {
      const address = payload.data[0]?.[index];
      const tokenId = payload.data[1]?.[index];
      if (!address || !tokenId) continue;
      const addressLower = address.toLowerCase();
      let addressData = contractData[addressLower];
      if (!addressData) {
        addressData = {};
        contractData[address] = addressData;
      }
      addressData[tokenId.toString()] = balance.toString();
    }
  }

  return response;
};

export { type BalanceResult };
